// داده‌ی داشبوردِ ادمینِ فنی (مایگریشن 0068).
//
// هر تابع این‌جا یک RPCِ `security definer` صدا می‌زند که **خودش** ادمین
// بودنِ صداکننده را چک می‌کند. یعنی پنهان کردنِ دکمه در رابط، لایه‌ی امنیتی
// نیست و لازم هم نیست باشد: کاربرِ عادی همین فراخوانی را بزند، `not allowed`
// می‌گیرد.
//
// چرا اسکیمای `crawler` مستقیم خوانده نمی‌شود: جدول‌های خامش بدنه‌ی کاملِ
// صفحه‌های گرفته‌شده و متنِ خطاها را دارند. باز کردنشان به PostgREST یعنی یک
// سیاستِ اشتباه، یک نشتِ کامل — نگاه کن به توضیحِ بالای مایگریشن 0068.
import { supabase } from '../lib/supabase';
import { FUNNEL_EVENTS } from '../lib/funnel';
import type { JourneyData } from '../lib/journeyReport';
import type { EventAddresses } from '../lib/addressReport';
import type { AliasEntriesData, AliasUsageData } from '../lib/aliasReport';
import type { AddressTableData } from '../lib/addressTable';
import type { OutOfBagReport } from '../lib/outOfBag';
import type { PromptDetail, PromptsData, TraceDetail, TraceKey } from '../lib/promptReport';
import type { AdminUsersData } from '../lib/usersReport';
import type { RankStatsData } from '../lib/rankReport';
import type { EventIssuesData, FixColumn } from '../lib/eventIssuesReport';
import type { HealthData, HealthExclusions, HealthSample } from '../lib/healthReport';
import type { ImageVerdict } from '../lib/suspectImages';

/**
 * یک عدد، دو بار — و این کلِ قراردادِ داشبورد است (مایگریشن 0108).
 *
 * ⚠ **انتخابگرِ پنجره و انتخابگرِ اجرا هر دو حذف شدند.** به‌جای اینکه آدم
 * یک بازه انتخاب کند و بعد یادش برود کدام را انتخاب کرده، هر عدد همیشه
 * دو (و جایی که معنی داشته باشد، سه) تاست:
 *
 *   `d10`    در ۱۰ روزِ گذشته
 *   `run`    در آخرین اجرای خطِ لوله
 *   `total`  روی هم رفته — اختیاری، چون بلوکِ «جریان»ِ کارنامه ندارد.
 *
 * ⚠ روی جدول‌هایی که upsert می‌شوند (کاندیدا، رویدادِ نهایی، حکمِ سیاست)
 * معنیِ `d10`/`run` **دست‌خورده** است، نه «ساخته‌شده»: کاندیدایی که در
 * آخرین اجرا دوباره دیده شد در `run` هست. برای همین `total` کنارشان
 * می‌ماند — بی آن، «۱۲ کاندیدا» معلوم نمی‌کند از ۱۲ تا یا از ۳۰ هزار تا.
 */
export interface Counts {
  total?: number;
  d10: number;
  run: number;
}

/** همان `Counts` ولی ثانیه — و `null`ش یعنی «نچرخید»، نه «صفر ثانیه». */
export interface Seconds {
  total: number | null;
  d10: number | null;
  run: number | null;
}

/**
 * شهرِ مجازیِ منابعی که در `crawler.sources` ردیف ندارند.
 *
 * روی داده‌ی واقعی این ۹۴ منبع از ۱۵۸ تاست: شناسه‌های `draft:…` که کرالر
 * پیدا کرده ولی هنوز به رجیستری ترفیع نگرفته‌اند. با یک join معمولی بی‌صدا
 * از نمودار می‌افتادند — و داشبوردی که بی‌صدا کم بشمارد از نبودنش بدتر است.
 *
 * تیلدا عمدی است: اسلاگِ واقعی فقط حروفِ کوچک و خط‌تیره/زیرخط دارد، پس این
 * مقدار هرگز با شهرِ واقعی اشتباه نمی‌شود. مقدارش با مایگریشن 0069 یکی است.
 */
export const UNKNOWN_CITY = '~unknown';

/**
 * شمارشِ خطِ لوله‌ی کرالر — مایگریشن 0090 (بازنویسیِ vNext).
 *
 * ⚠ **دو خانواده‌ی عدد، و قاطی کردنشان بدترین اشتباهِ این تایپ است:**
 *
 *   `win_*`  در پنجره‌ی انتخابی چه گذشت. فقط از جدول‌هایی می‌آید که فقط
 *            append می‌شوند و زمانِ واقعی دارند.
 *   بقیه     **وضعیتِ الان**. جدول‌های کاندیدا/خوشه/حکم upsert می‌شوند و
 *            ستونِ زمانشان با هر اجرا جلو می‌رود؛ فیلترِ ۳۰ روزه رویشان یا
 *            همه را می‌دهد یا هیچ‌کدام را — یعنی فیلتر نیست، قرعه است.
 *
 * زنجیره از `event_crawler/ARCHITECTURE-VNEXT.md` می‌آید:
 * Fetch → Detect → Expand → Claim → Mention → Match → Cluster → Merge →
 * Policy → Publish. اگر آن سند عوض شد، این‌جا هم باید عوض شود.
 */
/** سطل‌های نمودارِ «سرنوشتِ درخواست‌ها» — شناسه‌ی گره در `crawlerFlow`. */
export type FetchBucket =
  'ok' | 'unchanged' | 'error' | 'skipped' | 'robots' | 'tier' | 'disabled';

/** تازه‌ترین ردیفِ `crawler.fetch_events` از یک سطل. مایگریشنِ ۰۱۱۵. */
export interface FetchExample {
  source_id: string;
  url: string;
  http_status: number | null;
  detail: string | null;
  started_at: string;
}

export interface CrawlerFunnel {
  /** پنجره‌ی ثابت — ۱۰. در امضا نیست، در جواب هست تا رابط عددش را نبافد. */
  days: number;
  /** شهری که این اعداد رویش فیلتر شده‌اند؛ `null` یعنی همه. */
  city: string | null;
  /**
   * همیشه `true` است. عمداً می‌ماند: رابط نوارِ توضیحِ «هنوز کار نمی‌کند» را
   * به همین بسته، و اگر روزی دیتابیسی بدونِ `crawler.sources` بالا بیاید
   * باید دوباره بتواند صریح بگوید چرا.
   */
  city_filter_supported: boolean;
  /** لحظه‌ای که این اعداد اندازه گرفته شدند. */
  as_of: string;
  /** نسلِ خوشه‌بندی‌ای که همه‌ی شمارشِ خوشه به آن بند است. */
  generation_id: string | null;
  /** کِی ساخته شد — ستونِ «آخرین اجرا»ی شمارشِ خوشه به همین بند است. */
  generation_at: string | null;
  /**
   * آخرین اجرای **کاملِ** خطِ لوله؛ ستونِ دومِ **هر** عددِ این صفحه همین است.
   *
   * ⚠ «کامل» یعنی هر پنج فازِ زنجیره در آن اجرا روشن بوده — همان چیزی که
   * فقط اجرای شبانه می‌کند. کارِ صفِ ادمین (`inspect`/`test`) و هر
   * `crawl vnext <stage>`ِ دستی هم ردیفِ اجرا می‌سازند و اگر آن‌ها حساب
   * می‌شدند، ستون صفر و یک می‌شد (مایگریشنِ ۰۱۰۹).
   */
  run: {
    id: string;
    command: string | null;
    status: string | null;
    /** `false` یعنی هیچ اجرای کاملی ثبت نشده و این آخرین اجرای ناقص است. */
    full: boolean;
    started_at: string;
    finished_at: string;
  } | null;
  /** شروعِ همان اجرای بالا — یعنی اعدادِ این صفحه مالِ چه زمانی‌اند. */
  last_pipeline_run: string | null;
  /** تازه‌ترین اجرا از **هر** نوع؛ اگر با بالایی فرق دارد، یعنی کارِ دستی/صف بعدش دویده. */
  last_any_run: string | null;

  /** دیتابیس اسکیمای `crawler` را ندارد، یا این گزارش هنوز ساخته نشده. */
  error?: string;

  // ═══ Fetch ═══
  // ⚠ `fetch_events` جدولِ تازه‌ای است و سندهای قدیمی ردی در آن ندارند، پس
  // با `docs_*` جمع نمی‌خواند و نباید هم بخواند.
  fetch_requests: Counts;
  fetch_ok: Counts;
  fetch_unchanged: Counts;
  fetch_blocked: Counts;
  fetch_errors: Counts;

  /**
   * ⚠ **این هفت تا از مایگریشنِ ۰۱۱۵ آمده‌اند و اختیاری‌اند، عمداً.**
   * صفحه از کشِ `crawler.admin_reports` می‌خواند و آن را کرالر می‌سازد؛
   * تا اجرای بعدی‌اش جوابِ کش هنوز شکلِ قدیم را دارد. اجباری کردنشان یعنی
   * `undefined.d10` و یک صفحه‌ی سفید، به‌جای نموداری که یک شب دیرتر می‌آید.
   *
   * `fetch_skipped` بزرگ‌ترین عددِ این خانواده است و تا ۰۱۱۵ اصلاً کلیدی
   * نداشت: درخواستی که کرالر خودش کنار گذاشت، چون آن آدرس سه بار پشتِ سرِ
   * هم خطا داده و کشِ لینکِ مرده هفت روز نگهش می‌دارد. با `unchanged` —
   * که کشِ **واقعی** است — یکی نیست و قاطی کردنشان همان سوالی است که این
   * کلیدها برای جوابش اضافه شدند.
   */
  fetch_skipped?: Counts;
  fetch_blocked_robots?: Counts;
  fetch_blocked_tier?: Counts;
  fetch_blocked_disabled?: Counts;
  fetch_listing?: Counts;
  fetch_detail?: Counts;
  /** چند بار فازِ `fetch` روی **یک منبع** دوید — واحدِ ستونِ «اجرا»ی جدولِ فازها. */
  fetch_runs?: Counts;
  /**
   * یک ردیفِ **واقعی** از هر سطل، برای کارتِ شناورِ روی گره‌های نمودار.
   *
   * چرا این‌جا و نه در `samples`: آن گزارش به‌ازای هر «نوع» ردیفِ کشِ جدا
   * دارد و کلیدهایش را مخزنِ کرالر می‌سازد، پس نوعِ تازه دیپلویِ آن مخزن
   * می‌خواهد. این کلیدِ تازه‌ی همان گزارشِ موجود است.
   */
  fetch_examples?: Partial<Record<FetchBucket, FetchExample | null>>;

  /**
   * ⚠ **این خانواده از مایگریشنِ ۰۱۴۴ می‌آید و همه‌شان اختیاری‌اند** — به
   * همان دلیلی که خانواده‌ی ۰۱۲۱ اختیاری است: کش را کرالر می‌سازد و تا
   * اجرای بعدی‌اش جواب شکلِ قدیم را دارد. اجباری کردنشان یعنی صفحه‌ی سفید،
   * به‌جای بخشی که یک شب دیرتر می‌آید.
   *
   * ═══ سه مرحله‌ی **قبل از** گرفتنِ صفحه ═══
   *
   * تا ۰۱۴۴ نمودار از «گرفتنِ صفحه» شروع می‌شد، انگار فهرستِ منابع از آسمان
   * آمده. نیامده: کرالر خودش هم منبع پیدا می‌کند — کشف (از مدل می‌پرسد چه
   * سایت‌هایی در این شهر رویداد دارند) → بررسی (صفحه‌ی هر نامزد را واقعاً
   * می‌گیرد و نگاه می‌کند) → ترفیع (آن‌که به درد می‌خورد به رجیستری می‌رود).
   *
   * ⚠ فیلترِ شهرشان ستونِ `city`ِ خودِ آن جدول‌هاست و نه شناسه‌ی منبع:
   * پیش‌نویسی که هنوز ترفیع نگرفته `source_id` ندارد. پس با شهرِ مجازیِ
   * «بدونِ شهر» صفر می‌شوند و درست هم همین است.
   */
  disc_runs?: Counts;
  disc_candidates?: Counts;
  insp_total?: Counts;
  insp_usable?: Counts;
  /** اصلاً نشد نگاهش کنیم (۴۰۳، ۴۰۴، robots، تایم‌اوت). */
  insp_blocked?: Counts;
  /** نگاه کردیم و به درد نخورد. با «مسدود» یکی نیست و یکی کردنشان گمراه‌کننده است. */
  insp_unusable?: Counts;
  drafts_total?: Counts;
  drafts_promoted?: Counts;
  drafts_rejected?: Counts;
  drafts_pending?: Counts;
  /** دلیلِ ردِ ترفیع، به زبانِ خودِ کرالر. */
  drafts_reasons?: { reason: string; n: number }[];
  /** عددِ **حالت** است و نه جریان، پس هر سه ستونش یکی است. */
  sources_total?: Counts;
  sources_enabled?: Counts;
  sources_disabled?: Counts;

  /**
   * «فردا هم باز سراغش می‌رویم؟» — جوابش بله است.
   *
   * کشِ خطا جلوی **تماسِ شبکه** را می‌گیرد، نه جلوی وارد شدنِ آدرس به صف.
   * پس هر آدرسِ مرده هر شب دوباره سرِ صف می‌آید و دوباره رد می‌شود. این
   * ردیف‌ها همان تفاوت را به عدد در می‌آورند: چند درخواست، روی چند آدرسِ
   * **یکتا**. شناسه‌ی سطل عمداً همان شناسه‌ی گره‌های نمودارِ درخواست‌هاست.
   */
  fetch_repeat?: { bucket: FetchBucket | 'blocked_other'; requests: number; urls: number; sources: number }[];

  /**
   * ⚠ **این چهارتا دقیقاً `candidates` می‌شوند، در هر سه ستون.** تا ۰۱۴۴
   * «در صفِ کار» از تفریق در می‌آمد و هیچ دلیلی نداشت.
   *
   * ⚠ همه با زمانِ **خودِ کاندیدا** پنجره می‌خورند و نه با زمانِ جدولِ
   * برآیند. برای همین `cand_insufficient` جدا از `cand_no_mention` است و
   * کپیِ بی‌دلیل نیست: آن یکی با `built_at` پنجره می‌خورد و نمودارِ سنکی از
   * قبل رویش بنا شده. با زمان‌های قاطی، جمعِ چهار سطل روی داده‌ی واقعی
   * ۳٬۹۷۵ تا از ورودی بزرگ‌تر می‌شد — یعنی یک کاندیدا دو بار شمرده شده.
   */
  cand_built?: Counts;
  cand_insufficient?: Counts;
  cand_pending_no_build?: Counts;
  cand_pending_no_extract?: Counts;

  /** حکمِ `publish` دلیل ندارد و نباید هم داشته باشد: «آری» توضیح نمی‌خواهد. */
  policy_reasons?: { decision: string; reason: string; n: number }[];

  /**
   * ⚠ تفکیکی که یک دروغِ نیم‌بند را تعمیر می‌کند: «نرفته» را تفریق می‌ساخت و
   * `publishable_mapped` به **دفترِ خودِ کرالر** نگاه می‌کند. بیشترِ آن‌ها
   * در اپ هستند و کاربر می‌بیندشان؛ فقط کرالر ردیفِ دفترش را ننوشته.
   */
  notsent_in_app?: Counts;
  notsent_gone?: Counts;
  /** بیست ردیفِ واقعی با لینکِ سایتِ منبع — بدونِ لینک فقط یک عددِ دیگر است. */
  notsent_rows?: {
    title: string; url: string | null; source_id: string | null;
    why: 'in_app' | 'gone'; at: string;
  }[];

  claims: Counts;

  // ═══ سند و تشخیص ═══
  docs_listing: Counts;
  docs_detail: Counts;
  detect_documents: Counts;
  /** صفحه‌ای که تکه شد و **صفر** کاندیدا داد — خرابیِ بی‌صدای T4. */
  detect_barren: Counts;

  // ═══ کاندیدا ═══
  candidates: Counts;
  cand_claims_run: Counts;
  cand_claims_none: Counts;
  cand_expand_attached: Counts;
  /** فیلدِ کافی نداشت که بشود ادعای یک رویداد. بزرگ‌ترین ریزشِ کلِ خط. */
  cand_no_mention: Counts;

  mentions: Counts;
  /**
   * ⚠ استثنا: عضویتِ یک نسل یکجا نوشته می‌شود، پس `d10`/`run`ِ خوشه یا
   * همان `total` است یا صفر — بسته به اینکه آخرین خوشه‌بندی کِی بوده.
   */
  clusters: Counts;
  clusters_multi_source: Counts;

  match_same: Counts;
  match_related: Counts;
  /** ریزش نیست، ولی مهم‌ترین عددِ کیفیت: دو mention که شاید یکی بودند. */
  match_uncertain: Counts;
  match_different: Counts;

  events: Counts;
  /** ردیفِ canonical دارد ولی خوشه‌اش در آخرین نسل نیست — جانشینِ «شبح». */
  events_dead_cluster: Counts;
  events_no_url: Counts;
  events_no_date: Counts;

  policy_publish: Counts;
  policy_review: Counts;
  policy_reject: Counts;

  /** از `policy_publish`ها، چند تا واقعاً نگاشتِ انتشار دارند. */
  publishable_mapped: Counts;
  published: Counts;
  published_live: Counts;
  published_past: Counts;
  published_expired: Counts;
  orphaned: Counts;

  /** حکمِ آدم روی جفت‌های مشکوک. */
  labels_total: Counts;
}

/** یک فازِ خطِ لوله، از `crawler.runs`. */
export interface CrawlerStage {
  stage: string;
  /**
   * «این فاز به منبع بند است؟» روی داده‌ی واقعی فقط `fetch` چنین است؛ ده فازِ
   * دیگر یک‌جا روی همه‌چیز کار می‌کنند و `source_id` ندارند.
   *
   * پس آن‌ها با هر فیلترِ شهری دیده می‌شوند — وگرنه انتخابِ یک شهر جدول را
   * از ۱۱ ردیف به ۱ ردیف می‌رساند و سوالِ اصلی‌اش («کدام مرحله می‌شکند»)
   * بی‌جواب می‌ماند. رابط با همین پرچم علامتشان می‌زند تا اعدادشان به اشتباه
   * مالِ آن شهر خوانده نشود.
   */
  source_scoped: boolean;
  runs: Counts;
  failures: Counts;
  last_run: string | null;
  last_ok: string | null;
  /**
   * میانه و صدکِ ۹۵ کنارِ هم، نه میانگین: یک اجرای پنج‌دقیقه‌ای در میانِ
   * پنجاه اجرای ده‌ثانیه‌ای میانگین را بی‌معنی می‌کند، ولی خودش دقیقاً همان
   * چیزی است که باید دیده شود.
   */
  p50_seconds: Seconds;
  p95_seconds: Seconds;
  last_error: string | null;
  /** کلیدهایش از فازی به فاز دیگر فرق می‌کند، پس شکلِ ثابتی ندارد. */
  counts: Record<string, number> | null;
  /** همان، ولی فقط در آخرین اجرا. */
  counts_run: Record<string, number> | null;
}

export interface CrawlerSource {
  source_id: string;
  /** شهرِ رجیستری، یا `UNKNOWN_CITY` اگر منبع در رجیستری نباشد. */
  city: string;
  documents: Counts;
  /** درخواستی که `error` یا `blocked` شد. */
  documents_failed: Counts;
  candidates: Counts;
  mentions: Counts;
  /** خوشه‌ی **زنده**ای که این منبع عضوی در آن دارد — مثل قیف، نسل‌دار. */
  events: Counts;
  published: Counts;
  fetch_runs: Counts;
  last_fetch_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
}

/**
 * یک ردیفِ فهرستِ کشوییِ شهر.
 *
 * چرا کشویی و نه فیلدِ متنی: کاربر باید اسلاگ را حدس می‌زد، و یک غلطِ تایپی
 * («Ottawa» به‌جای «ottawa») داشبوردی سراسر صفر می‌داد که از خطا بدتر است،
 * چون شبیهِ جوابِ درست به نظر می‌رسد.
 */
export interface CrawlerCity {
  city: string;
  sources: number;
  published: number;
}

/**
 * وضعیت‌هایی که می‌شود چند ردیفِ واقعی‌شان را دید. فهرست **بسته** است و
 * دیتابیس نوعِ ناشناخته را با خطا رد می‌کند، نه با آرایه‌ی خالی — آرایه‌ی
 * خالی شبیهِ «چیزی نیست» است در حالی که یعنی «اسمش را غلط نوشتی».
 */
export type SampleKind =
  | 'failed_fetch' | 'barren_listing' | 'no_claims' | 'no_mention'
  | 'uncertain_match' | 'no_date' | 'no_url'
  | 'policy_review' | 'policy_reject' | 'published';

/**
 * «کرالر و اپ با هم می‌خوانند؟» — مایگریشن 0089، که خودش فقط از دو ویوی
 * مخزنِ event_crawler (مایگریشنِ ۰۰۳۲ی آن) می‌خواند.
 *
 * وقتی این دو ویو روی دیتابیس نیستند، به‌جای خطا یک `error` برمی‌گردد. دو
 * حالتش عمداً جداست و رابط هم جدا نشانشان می‌دهد:
 *   `crawler-schema-missing` — این دیتابیس اصلاً اسکیمای کرالر را ندارد.
 *   `sync-view-missing`      — دارد، ولی مایگریشنِ ۰۰۳۲ رویش نخورده.
 */
export type PublishSyncError = 'crawler-schema-missing' | 'sync-view-missing';

/** خلاصه — همان یک سطرِ `crawler.v_ck_publish_sync`. */
export interface PublishSyncSummary {
  /** هیچ ناهماهنگی‌ای نیست. */
  in_sync_overall: boolean;
  /** «آماده‌ی انتشار» به تعریفِ خودِ publisher: خوشه‌ی زنده + حکمِ روزآمدِ publish. */
  ready_total: number;

  /** ردیفِ خودش در اپ هست و هر چهار فیلد یکی است. */
  in_sync: number;
  /**
   * تا شده در یک سری: ردیفِ خودش را ندارد و این **خرابی نیست** — داده‌اش
   * زیرِ والدِ سری به‌شکلِ `event_occurrence` زنده است. بدونِ جدا شمردنش،
   * صدها رویدادِ سالم «گم‌شده» گزارش می‌شدند.
   */
  in_sync_as_occurrence: number;
  archived_past: number;
  admin_decision: number;

  /** در اپ هست ولی محتوایش عوض شده (عنوان/تاریخ/آدرس). */
  content_drift: number;
  hidden_in_app: number;
  app_row_missing: number;
  never_published: number;
  /** جهتِ مخالف: در اپ منتشر است ولی کرالر امروز منتشرش نمی‌کند. */
  orphan_in_app: number;
  /** نگاشتی که ردیفِ اپش دیگر وجود ندارد. */
  dangling_map: number;

  out_of_sync_total: number;
  /**
   * مخرجش `ready_total + orphan_in_app` است و نه `ready_total` تنها: یتیم‌ها
   * بخشی از «آماده» نیستند ولی بخشی از ناهماهنگی‌اند، و با مخرجِ کوچک‌تر
   * درصد می‌توانست از ۱۰۰ رد شود.
   */
  out_of_sync_pct: number;

  last_published_at: string | null;
  /** ثانیه، نه `interval`؛ قالب‌بندی کارِ رابط است. */
  since_last_publish_seconds: number | null;
  published_last_24h: number;
}

/** شمارشِ state×reason روی **کلِ** ویو، بی سقفِ ردیف. */
export interface PublishSyncGroup {
  side: 'crawler_ready' | 'app_published';
  state: string;
  reason: string | null;
  n: number;
}

export interface PublishSync {
  error?: PublishSyncError;
  summary?: PublishSyncSummary;
  groups?: PublishSyncGroup[];
}

/** یک ردیفِ `crawler.v_ck_publish_sync_detail`. */
export interface PublishSyncRow {
  side: 'crawler_ready' | 'app_published';
  state: string;
  reason: string | null;
  cluster_id: string | null;
  external_id: string | null;
  app_event_id: string | null;
  /** طرفِ کرالر. */
  title: string | null;
  starts_at: string | null;
  /** طرفِ اپ — کنارِ هم می‌آیند تا `content_drift` بدونِ کوئریِ دوم دیده شود. */
  app_title: string | null;
  app_starts_at: string | null;
  app_status: string | null;
  published_at: string | null;
}

export interface PublishSyncDetail {
  error?: PublishSyncError;
  /** پیش از سقفِ ردیف. سقف هرگز بی‌صدا نمی‌ماند. */
  total?: number;
  rows?: PublishSyncRow[];
}

/** دو سطحی که `app_events.surface` می‌تواند باشد (مایگریشن 0068). */
export type SurfaceKey = 'web' | 'mobile';

/** همان کارت‌های بالای سربرگِ فعالیت، برای یک سطح. */
export interface SurfaceCounts {
  total: Counts;
  sessions: Counts;
  people: Counts;
  guests: Counts;
}

/**
 * کاربرِ واردشده، به تفکیکِ اینکه کدام سطح‌ها را دیده.
 *
 * ⚠ فقط کاربرِ **واردشده**: مهمان شناسه‌ای ندارد که بشود گفت نشستِ وب و
 * نشستِ موبایلش مالِ یک نفر است. حدس زدنش یعنی ساختنِ عددی که هیچ‌جا
 * قابلِ راستی‌آزمایی نیست.
 */
export interface PeopleMix {
  web_only: number;
  mobile_only: number;
  both: number;
}

/**
 * ⚠ **ستونِ `run` این‌جا «۲۴ ساعتِ اخیر» است، نه «آخرین اجرا».** این جدول
 * را کاربر پر می‌کند نه کرالر و اجرای شبانه‌ای در کار نیست؛ رابط هم همین
 * را می‌نویسد. برچسبِ دروغ از ستونِ نداشته بدتر است.
 */
/** جوابِ `admin_funnel` — فقط شمارش؛ درصد و حکم در `lib/activityReport.ts`. */
export interface FunnelData {
  days: number;
  steps: { name: string; total: number; web: number; mobile: number }[];
  /** یک ویژگیِ کوتاهِ قدم (`source`، `from`، `kind`، …) با تعدادِ آدم‌ها. */
  details: { name: string; key: string; value: string; surface: string; people: number }[];
}

/**
 * جوابِ `admin_retention` (مایگریشن 0162، WHOSUP-310) — فقط شمارش؛ درصد و
 * «هنوز زود است» در `lib/activityReport.ts`. فقط کاربرِ واردشده: مهمان در
 * `app_events` شناسه‌ی ثابتی ندارد.
 */
export interface RetentionData {
  weeks: number;
  from: string;
  /** هر (هفته، سطح) یک ردیف؛ `surface` یکی از `all` / `web` / `mobile`. */
  cohorts: {
    week: string; surface: string; new: number;
    d1_ready: number; d1_back: number; w_ready: number; w_back: number;
  }[];
  /** آخرین صفحه‌ی کسانی که در روزهای ۱ تا ۸ برنگشتند؛ `''` = ثبت نشده. */
  lost: { week: string; surface: string; screen: string; people: number }[];
}

export interface AppActivity {
  days: number;
  total: Counts;
  sessions: Counts;
  people: Counts;
  guests: Counts;
  /**
   * همان چهار عددِ بالا، این بار جدا برای هر سطح (مایگریشن 0148).
   *
   * ⚠ جمعِ `people`ِ دو سطح از `people`ِ کل بیشتر می‌شود و این باگ نیست:
   * کسی که هم روی گوشی و هم روی سایت وارد شده در هر دو شمرده می‌شود.
   * `people_mix` همان را باز می‌کند.
   *
   * هر دو کلیدِ `web` و `mobile` همیشه هستند، حتی وقتی صفرند — صفر خودش
   * یک خبر است.
   */
  surfaces: Record<SurfaceKey, SurfaceCounts>;
  /** «کجا هردو»: کاربرِ واردشده به تفکیکِ اینکه کدام سطح‌ها را دیده. */
  people_mix: { total: PeopleMix; d10: PeopleMix };
  by_name: {
    name: string; n: number; n_run: number; sessions: number;
    /** ⚠ `n_web + n_mobile === n`؛ رویدادی که یکی‌شان صفر باشد یک‌طرفه است. */
    n_web: number; n_mobile: number;
    /** همان `n_run`، جدا برای هر سطح. */
    run_web: number; run_mobile: number;
    s_web: number; s_mobile: number;
  }[];
  by_day: {
    day: string; n: number; sessions: number;
    n_web: number; n_mobile: number; s_web: number; s_mobile: number;
  }[];
  recent: { name: string; surface: string; props: unknown; created_at: string }[];
  /**
   * چیزی که در هیچ‌کدام از اعدادِ بالا نیست: حساب‌های داخلی — خودِ ما و
   * کاربرهای تستی (مایگریشن 0119).
   *
   * ⚠ عمداً در جواب می‌آید و رابط هم نشانش می‌دهد. فیلترِ بی‌صدا یعنی
   * کسی که این صفحه را نگاه می‌کند نمی‌داند دارد کدام جهان را می‌بیند.
   *
   * `events` ردیفِ کنارگذاشته‌شده است (شاملِ ردیف‌های مهمانِ همان نشست‌ها)،
   * `accounts` و `names` فقط حساب‌هایی که واقعاً رویدادی داشتند.
   */
  excluded: { events: number; accounts: number; names: string[] };
}

/**
 * یک بازخوردِ کاربر، همان‌طور که در فهرست دیده می‌شود (مایگریشن 0113).
 *
 * ⚠ متنِ خودِ کاربر در `text` است و **از دیتابیس خام می‌آید** — هر چیزی که
 * در فرم نوشته شده. هر جایی که نمایش داده می‌شود باید متن باشد و نه HTML.
 */
export interface FeedbackItem {
  id: number;
  at: string;
  /** `bug` | `idea` | `other` — با `feedbackKind()` در `lib/feedback` خوانده شود. */
  kind: string;
  text: string;
  app_version: string | null;
  platform: string | null;
  user_id: string | null;
  who: string | null;
  username: string | null;
  /** شناسه‌ی لاگِ تشخیصی، اگر کاربر تیکش را زده باشد. */
  diag: string | null;
  diag_id: number | null;
  /** چند خطای خودکارِ همان کاربر، در نیم‌ساعتِ پیش از گزارش. */
  errors_near: number;
}

export interface FeedbackList {
  /** کلِ بازخوردها، مستقل از سقفِ فهرست. */
  total: number;
  items: FeedbackItem[];
}

/** لاگِ تشخیصیِ چسبیده به یک بازخورد — چند کیلوبایت متنِ آماده‌ی خواندن. */
export interface FeedbackDiag {
  id: number;
  at: string;
  session: string | null;
  /** چند سطر در کلِ نشست ساخته شده (با سطرهای نگه‌داشته‌شده فرق دارد). */
  seen: string | null;
  /** اگر لاگ خودش را خاموش کرده باشد، دلیلش. */
  stopped: string | null;
  lines: string | null;
}

export interface FeedbackDetail extends Omit<FeedbackItem, 'diag_id' | 'errors_near'> {
  locale: string | null;
  diag_log: FeedbackDiag | null;
  errors: { at: string; where_at: string; message: string }[];
}

/**
 * یک گزارشِ محتوا/کاربر، همان‌طور که صفِ ادمین می‌بیندش (مایگریشن 0135).
 *
 * ⚠ `target`، `note` و `author` همگی **متنِ خامِ کاربر**اند — عنوانِ سیگنالی
 * که یکی نوشته، خودِ پیامی که گزارش شده، و توضیحِ گزارش‌دهنده. هر جا نمایش
 * داده می‌شوند باید متن باشند و نه HTML.
 *
 * `target_kind`/`reason`/`status` رشته‌اند و نه اتحادِ تایپ‌ها: از دیتابیس
 * می‌آیند و تایپ‌چک هیچ تضمینی درباره‌شان نمی‌دهد. خواندنشان با
 * `lib/contentReport` است، که مقدارِ ناشناخته را هم بی‌سروصدا برچسب می‌زند.
 */
/** یک اعتراض به فیلترِ واژه‌ها — `admin_filter_appeals` (مایگریشن 0160). */
export interface FilterAppeal {
  id: number;
  at: string;
  field: 'signal' | 'message' | 'profile_name';
  body: string;
  matched: string[];
  status: 'open' | 'allowed' | 'upheld';
  user: string;
  /** سیگنالِ نگه‌داشته‌ای که این اعتراض به آن وصل است (0174). */
  signal_id?: string | null;
  /** پیامِ پنهان‌شده‌ای که این اعتراض به آن وصل است (0175). */
  message_id?: number | null;
  /** پر یعنی مدیر نگهش داشته بود، نه فیلتر: دلیل و شرطِ خودش (0175). */
  held_reason?: string | null;
  held_fix?: string | null;
}

export interface ContentReportItem {
  id: number;
  at: string;
  reason: string;
  note: string | null;
  status: string;
  target_kind: string;
  /** گزارش‌دهنده: «نامِ نمایشی (@username)». */
  reporter: string;
  /** خلاصه‌ی خودِ چیزی که گزارش شده — عنوانِ سیگنال، متنِ پیام، یا نامِ پروفایل. */
  target: string;
  /** کسی که کار به او برمی‌گردد: سازنده‌ی سیگنال، فرستنده‌ی پیام، یا خودِ آن پروفایل. */
  author: string;
  author_id: string | null;
  /** محتوا با «حذفِ محتوا» رفته؛ `target` همان متنِ پیش از حذف است (0173). */
  removed?: boolean;
  /** نویسنده اخراج شده (0173). */
  author_banned?: boolean;
  /** برای پیام: سیگنالی که تویش گفته شده. برای بقیه null. */
  context: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  admin_note: string | null;
}

export interface ContentReportList {
  total: number;
  open: number;
  actioned: number;
  dismissed: number;
  items: ContentReportItem[];
}

/** شمارش‌های سربرگِ «عکس‌ها» (مایگریشن 0133). فقط رویدادهای زنده. */
export interface EventPhotoTotals {
  live_events: number;
  with_photo: number;
  /** از آن‌ها چندتا «غیر معتبر»اند و به کاربر نشان داده نمی‌شوند (0135). */
  hidden: number;
  hosts: number;
  /** آدرس‌هایی که بیش از یک رویدادِ زنده استفاده‌شان می‌کند، و جمعِ آن رویدادها. */
  dup_urls: number;
  dup_events: number;
  failed_urls: number;
  failure_reports: number;
  /** ردیف‌های شمرده‌نشده (rejected/merged/…) — همان صداقتِ 0119. */
  excluded: number;
}

export interface EventPhotoHost {
  /** null یعنی آدرسِ بدشکلی که دامنه‌ای ازش درنیامد. */
  host: string | null;
  photos: number;
  urls: number;
  dup_urls: number;
  max_repeat: number;
}

export interface EventPhotoSource {
  source: string;
  events: number;
  with_photo: number;
  dup_urls: number;
  dup_events: number;
  max_repeat: number;
  /** پرتکرارترین آدرسِ همین منبع — فقط وقتی واقعاً تکراری است (uses ≥ 2). */
  top_url: string | null;
}

export interface EventPhotoDup {
  url: string;
  host: string | null;
  uses: number;
  sources: number;
  /** تا سه عنوانِ نمونه از رویدادهایی که این عکس رویشان نشسته. */
  titles: string[];
  /** «غیر معتبر» علامت خورده؟ (مایگریشن 0135) — چک‌باکس از همین می‌آید. */
  invalid: boolean;
}

export interface EventPhotoFailure {
  url: string;
  host: string | null;
  reports: number;
  /** کاربرِ واردشده‌ی یکتا — مهمان‌ها فقط در reports شمرده می‌شوند. */
  users: number;
  last_at: string;
  /** روی چند رویدادِ زنده هنوز همین آدرس نشسته؛ صفر یعنی خودش رفع شده. */
  live_events: number;
  title: string | null;
  /** «غیر معتبر» علامت خورده؟ (مایگریشن 0135) */
  invalid: boolean;
}

/** یک عکسِ «غیر معتبر» — برای کارتِ خودش و برگرداندنش. */
export interface EventPhotoInvalid {
  url: string;
  host: string | null;
  marked_at: string;
  /**
   * روی چند رویداد نشسته. برخلافِ 0134 این عدد **باید** بزرگ‌تر از صفر باشد:
   * آدرس پاک نمی‌شود، فقط پنهان می‌شود.
   */
  on_events: number;
  /**
   * از آن‌ها چندتا هنوز نشان داده می‌شوند — یعنی پرچمشان جا مانده. باید صفر
   * باشد؛ هر عددِ دیگری یک خرابی است و رابط قرمز نشانش می‌دهد.
   */
  shown: number;
}

export interface EventPhotos {
  totals: EventPhotoTotals;
  by_host: EventPhotoHost[];
  by_source: EventPhotoSource[];
  top_dups: EventPhotoDup[];
  failures: EventPhotoFailure[];
  invalid: EventPhotoInvalid[];
}

/**
 * «چند عکسی» — مایگریشن 0150 (WHOSUP-244). عکس‌های اضافه‌ای که کرالر از
 * WHOSUP-240 به بعد در `events.metadata.images` می‌گذارد.
 */
export interface EventMultiPhotoTotals {
  live_events: number;
  with_photo: number;
  /** کلیدِ `images` را دارند، هر چه باشد — بیشتر از with_list یعنی فهرستِ یک‌عضوی/بدشکل. */
  with_key: number;
  /** فهرستِ دو عکس یا بیشتر. */
  with_list: number;
  /** روی‌هم چند عکس در همین فهرست‌ها (عکسِ اصلی هم شمرده می‌شود). */
  images: number;
  /** به سقفِ ۵ خورده‌اند. */
  capped: number;
  /** عضوِ اول = `image_url` — باید با with_list برابر باشد. */
  first_is_main: number;
  /** عضوِ اول `selected` دارد — باید با with_list برابر باشد. */
  first_selected: number;
  /** عکس‌های فهرست که «غیر معتبر» علامت خورده‌اند (0135). */
  invalid_in_list: number;
  /** رویدادهایی که دست‌کم یک «این‌ها یکی هستند» دارند (0151). */
  same_marked: number;
}

export interface EventMultiPhotoImage {
  url: string;
  source: string | null;
  /** صفحه‌ای که عکس از آن آمده. */
  page: string | null;
  selected: boolean;
  invalid: boolean;
  /** گروهِ «این‌ها یکی هستند» درونِ همین رویداد (0151)؛ null = تنها. */
  group: string | null;
}

export interface EventMultiPhotoSample {
  id: string;
  title: string;
  source: string;
  /** لینکِ رویداد روی منبع. */
  url: string | null;
  starts_at: string | null;
  image_url: string | null;
  image_invalid: boolean;
  n: number;
  images: EventMultiPhotoImage[];
}

export interface EventMultiPhotos {
  totals: EventMultiPhotoTotals;
  by_size: { n: number; events: number }[];
  by_source: { source: string; events: number; with_list: number; images: number; max_n: number }[];
  samples: EventMultiPhotoSample[];
}

/** `admin_suspect_images` — مایگریشن 0161 (WHOSUP-300). */
export interface SuspectImageSample {
  url: string;
  users: number;
  sessions: number;
  reports: number;
  last_reported_at: string;
  checked_at: string | null;
  http_status: number | null;
  content_type: string | null;
  verdict: ImageVerdict | null;
}

export interface SuspectImages {
  totals: { suspect: number; broken: number; hotlink_blocked: number; ok: number; untested: number };
  samples: SuspectImageSample[];
}

/**
 * نمونه‌های یک مرحله، دسته به دسته — مایگریشنِ ۰۱۴۵.
 *
 * ⚠ `bucket` عیناً همان `id`ِ سرنوشت‌ها در `lib/stageReport.ts` است. رابط
 * برچسب و توضیحِ فارسی را از همان‌جا برمی‌دارد و این‌جا فقط داده می‌آید،
 * وگرنه متنِ یک مفهوم دو نسخه پیدا می‌کرد — یکی در تایپ‌اسکریپت و یکی در SQL.
 *
 * دسته‌ای که ردیفی ندارد **اصلاً نمی‌آید**؛ آرایه‌ی خالی فرستاده نمی‌شود.
 */
export interface StageSampleRow {
  title: string | null;
  url: string | null;
  /** یک واقعیتِ کوتاه که همان دسته را معنی می‌کند: حکم، شمارِ ادعا، دلیل. */
  note: string | null;
  at: string | null;
}

export interface StageSamples {
  stage: string;
  /** پنجره‌ی نمونه‌ها بر حسبِ روز — همان ستونِ اولِ نمودار. */
  days: number;
  buckets: { bucket: string; rows: StageSampleRow[] }[];
  /** دیتابیس اسکیمای `crawler` را ندارد. */
  error?: string;
}

const call = async <T>(fn: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  return data as T;
};

export const opsRepo = {
  crawlerFunnel: (city?: string | null) =>
    call<CrawlerFunnel>('admin_crawler_funnel', { p_city: city ?? null }),

  /**
   * فازها و منابع هم `p_city` می‌گیرند و این اختیاری نیست: بدونش نمودار
   * فیلتر می‌شد و جدولِ زیرش نه، و دو عددِ متناقض روی یک صفحه از نبودِ فیلتر
   * گیج‌کننده‌تر است — آدم به هیچ‌کدام نمی‌تواند اعتماد کند.
   */
  crawlerStages: (city?: string | null) =>
    call<CrawlerStage[]>('admin_crawler_stages', { p_city: city ?? null }),

  crawlerSources: (city?: string | null) =>
    call<CrawlerSource[]>('admin_crawler_sources', { p_city: city ?? null }),

  /** شهرهایی که واقعاً منبع دارند — برای پر کردنِ کشویی. */
  crawlerCities: () => call<CrawlerCity[]>('admin_crawler_cities', {}),

  /**
   * چند ردیفِ واقعی از یک وضعیت.
   *
   * چرا لازم است: عددِ «۱۱۵ صفحه‌ی بی‌آیتم» می‌گوید چیزی خراب است ولی
   * نمی‌گوید چه چیزی. یک نگاه به سه تا از آن‌ها معمولاً کافی است — و
   * داشبوردِ بی‌نمونه آدم را برای هر سوال به psql می‌فرستد.
   */
  samples: <T = Record<string, unknown>>(kind: SampleKind, limit = 5) =>
    call<T[]>('admin_crawler_samples', { p_kind: kind, p_limit: limit }),

  /**
   * سه ردیفِ واقعی برای **هر دسته**ی یک مرحله‌ی نمودارِ «مرحله‌به‌مرحله»
   * (مایگریشنِ ۰۱۴۵).
   *
   * ⚠ بر خلافِ بقیه‌ی این خانواده از کش نمی‌خواند و اسکیمای `crawler` را
   * زنده می‌خواند — و همین است که اجازه می‌دهد جدولِ هر سیزده مرحله وجود
   * داشته باشد. دلیلش کاملِ سرِ همان مایگریشن است، خلاصه‌اش: صد ردیفِ نمونه
   * در پیلودِ هر شهر یعنی کشی چند برابر بزرگ‌تر و اجرای شبانه‌ای گران‌تر،
   * برای دیتایی که ۹۹٪ وقت‌ها هیچ‌کس بازش نمی‌کند.
   *
   * ⚠ پس **تنبل صدا زده شود**: فقط وقتی که آدم همان جدول را باز کند. هر
   * صدا زدنش حدودِ یک تا سه ثانیه است، چون پشتش کوئریِ واقعی روی جدول‌های
   * خام است و نه خواندنِ یک ردیفِ کش.
   */
  crawlerStageSamples: (stage: string, city?: string | null, limit = 3) =>
    call<StageSamples>('admin_crawler_stage_samples', {
      p_stage: stage, p_city: city ?? null, p_limit: limit,
    }),

  /**
   * خلاصه‌ی هماهنگیِ کرالر با اپ + شمارشِ state×reason.
   *
   * بدونِ پارامترِ پنجره‌ی زمانی، برخلافِ بقیه‌ی این فایل: این یکی «الان چه
   * وضعی است» را می‌پرسد نه «در N روزِ گذشته چه شد».
   */
  publishSync: () => call<PublishSync>('admin_publish_sync', {}),

  /**
   * ردیف‌های واقعی. بدونِ `state`، دو حالتِ سالم بیرون می‌مانند — وگرنه سقفِ
   * ردیف با آن‌ها پر می‌شد و حتی یک ردیفِ ناهماهنگ دیده نمی‌شد.
   */
  publishSyncDetail: (state?: string | null, reason?: string | null, limit = 200) =>
    call<PublishSyncDetail>('admin_publish_sync_detail', {
      p_state: state ?? null, p_reason: reason ?? null, p_limit: limit,
    }),

  activity: () => call<AppActivity>('admin_app_activity', {}),

  /**
   * آدم‌های یکتا در هر قدمِ قیف (مایگریشن 0161، WHOSUP-292). نام‌ها همیشه
   * `FUNNEL_EVENTS` است و از همین‌جا فرستاده می‌شود — SQL هیچ نامی را
   * دستی نمی‌داند.
   */
  funnel: (days = 10) =>
    call<FunnelData>('admin_funnel', { p_names: [...FUNNEL_EVENTS], p_days: days }),

  /** برگشتِ تازه‌واردها، هفته‌به‌هفته (مایگریشن 0162، WHOSUP-310). */
  retention: (weeks = 12) => call<RetentionData>('admin_retention', { p_weeks: weeks }),

  /**
   * فهرستِ کاربران با آمارِ فعالیتشان (مایگریشن 0149).
   *
   * ⚠ باز هم واقعیت و نه گزارش: «این حساب خوابیده است» را
   * `lib/usersReport.buildUsersReport` می‌گوید، نه SQL. همان دلیلِ
   * `journeys()` بالا.
   *
   * ⚠ این جوابْ **ایمیلِ کاربران** را دارد. تنها مصرف‌کننده‌اش سربرگِ
   * «کاربران» در اپِ ادمین است و خودِ RPC هم بی ادمین بودن جواب نمی‌دهد؛
   * چراییِ آمدنِ ایمیل سرِ همان مایگریشن نوشته شده.
   *
   * سقفش سخاوتمند است چون این جدول کوچک می‌ماند، و `matched`/`shown` در
   * جواب می‌گویند اگر واقعاً به سقف خورد.
   */
  users: (q?: string | null, limit = 500) =>
    call<AdminUsersData>('admin_users', { p_q: q ?? null, p_limit: limit }),

  /**
   * اجزای خامِ ترتیبِ پیشنهادی، یک ردیف به ازای هر رویدادِ زنده (مایگریشن
   * 0157، WHOSUP-285).
   *
   * ⚠ باز هم واقعیت و نه امتیاز: درصدِ پر بودن، کاملیِ آدرس و عددِ قیمت را
   * `lib/rankReport` می‌سازد، نه SQL.
   */
  rankStats: (limit = 3000) =>
    call<RankStatsData>('admin_event_rank_stats', { p_limit: limit }),

  /**
   * علامتِ «حسابِ داخلی/تستی» — یعنی از آمارِ رفتارِ کاربران بیرونش بگذار.
   *
   * ⚠ خاموش کردنش فقط روی حسابی جواب می‌دهد که **دستی** علامت خورده
   * باشد. حسابی که ایمیلش با الگوهای `is_internal_email` می‌خواند خطا
   * برمی‌گرداند و نه «انجام شد»؛ دلیلش سرِ مایگریشن 0149 است.
   */
  setInternal: (userId: string, internal: boolean, reason = '') =>
    call<{ user_id: string; internal: boolean; source: string | null }>(
      'admin_set_internal',
      { p_user_id: userId, p_internal: internal, p_reason: reason },
    ),

  /**
   * سفرِ کاربران — دادهٔ **خام**، نه نمودار (مایگریشن 0120).
   *
   * ⚠ ساختنِ نمودار و آمارِ صفحه‌ها کارِ `core/lib/journeyReport` است و نه
   * کارِ SQL: آستانه‌ها (چند پله، چند صفحه در هر ستون) تصمیمِ نمایشی‌اند و
   * عوض کردنشان نباید مایگریشن بخواهد. همان تقسیمِ کاری که `sourceTest.ts`
   * دارد.
   *
   * سقفِ ۳۰۰ نشست عمدی است و رابط می‌گویدش: پنجره‌ی «تازه‌ترین ۳۰۰ نشست»
   * از «۳۰ روزِ گذشته» صادق‌تر است، چون روی دیتابیسی که هفته‌ای صد نشست
   * دارد و دیتابیسی که روزی هزار، هر دو یک‌جور خوانده می‌شود.
   */
  journeys: (limit = 300) => call<JourneyData>('admin_journeys', { p_limit: limit }),

  /**
   * بازخوردهای کاربرها، تازه‌ترین اول (مایگریشن 0113).
   *
   * سقفش سخاوتمند است چون این جدول کوچک می‌ماند: بازخورد را آدم می‌نویسد،
   * نه اپ. صفحه‌بندی وقتی معنی پیدا می‌کند که واقعاً به سقف بخورد.
   */
  feedback: (limit = 100) => call<FeedbackList>('admin_feedback', { p_limit: limit }),

  /** همان یکی، با لاگِ تشخیصی و خطاهای دورِ همان لحظه. */
  feedbackDetail: (id: number) => call<FeedbackDetail>('admin_feedback_detail', { p_id: id }),

  /**
   * صفِ گزارش‌های محتوا و کاربر (مایگریشن 0135).
   *
   * ⚠ از راهِ RPC می‌آید و نه `from('content_reports')`، با اینکه سیاستِ RLS
   * به ادمین اجازه‌ی خواندنِ جدول را می‌دهد: ادمین عضوِ آن سیگنال نیست، پس
   * متنِ پیام و عنوانِ سیگنال با سیاستِ خودِ آن جدول‌ها از او پنهان می‌مانند.
   * صفی که محتوای گزارش‌شده را نشان نمی‌دهد، تصمیم‌گرفتنی نیست.
   *
   * `status` خالی یعنی همه.
   */
  contentReports: (status?: string | null, limit = 200) =>
    call<ContentReportList>('admin_content_reports', {
      p_status: status ?? null, p_limit: limit,
    }),

  /** رسیدگی به یک گزارش: `actioned` / `dismissed` / برگرداندن به `open`. */
  resolveReport: (id: number, status: string, note?: string | null) =>
    call<void>('admin_resolve_report', { p_id: id, p_status: status, p_note: note ?? null }),

  /**
   * «حذفِ محتوا» برای یک گزارش (مایگریشن 0175): سیگنال و پیام از همه جز
   * صاحبشان **پنهان** می‌شوند (نه پاک)، نام/عکس/وضعیتِ پروفایل به حالتِ
   * پیش‌فرض. دلیل اجباری است؛ همراهِ «در چه صورتی دوباره دیده می‌شود» به
   * صاحبش اعلان می‌شود. گزارش «رسیدگی شد».
   */
  removeReportedContent: (id: number, reason: string, fix: string | null) =>
    call<void>('admin_remove_reported_content', { p_id: id, p_reason: reason, p_fix: fix }),

  /**
   * «اخراجِ کاربر» (مایگریشن 0160): ورودش برای همیشه بسته، نشست‌هایش بسته،
   * سیگنال‌هایش حذف، و از 0175 دوستی‌هایش هم پاک. حساب پاک نمی‌شود.
   */
  ejectUser: (userId: string) =>
    call<void>('admin_eject_user', { p_user: userId }),

  /** اعتراض‌ها به فیلترِ واژه‌ها (مایگریشن 0160). `null` یعنی همه. */
  filterAppeals: (status: string | null = 'open') =>
    call<FilterAppeal[]>('admin_filter_appeals', { p_status: status }),

  /** `allowed` = فیلتر اشتباه گرفت، `upheld` = درست گرفت. */
  resolveFilterAppeal: (id: number, status: 'open' | 'allowed' | 'upheld') =>
    call<void>('admin_resolve_filter_appeal', { p_id: id, p_status: status }),

  /**
   * پرچم‌هایی که کاربرها روی رخدادها زده‌اند (`item_issues`) + تصمیمِ ادمین
   * و خودِ رخدادها — سربرگِ «پرچم‌های رخداد» (مایگریشن 0200). تفسیرِ متن و
   * آمار در `lib/eventIssuesReport.ts`.
   */
  eventIssues: (limit = 5000) =>
    call<EventIssuesData>('admin_event_issues', { p_limit: limit }),

  /** تایید / رد / دوباره باز؛ شمارِ پرچم‌هایی که واقعاً عوض شدند برمی‌گردد. */
  reviewEventIssues: (ids: number[], verdict: 'accepted' | 'rejected' | 'open', note?: string | null) =>
    call<number>('admin_review_event_issues', { p_ids: ids, p_verdict: verdict, p_note: note ?? null }),

  /**
   * اصلاحِ درجای رخداد. `patch` فقط ستون‌های عوض‌شده؛ `tagIds` `null` یعنی
   * تگ‌ها دست نخورند. پرچم‌های `issueIds` «تاییدشده و اصلاح‌شده» می‌شوند و
   * **می‌مانند**.
   */
  fixEvent: (
    eventId: string,
    patch: Partial<Record<FixColumn, string | boolean | null>>,
    tagIds: number[] | null,
    issueIds: number[],
    note?: string | null,
  ) => call<{ changed: string[]; locked: string[] }>('admin_fix_event', {
    p_event: eventId, p_patch: patch, p_tag_ids: tagIds,
    p_issue_ids: issueIds.length ? issueIds : null, p_note: note ?? null,
  }),

  /**
   * گزارشِ عکسِ رویدادها (مایگریشن 0133): از کجا می‌آیند، کدام‌ها تکراری‌اند
   * و کدام‌ها روی دستگاهِ کاربر لود نشدند. دادهٔ «لود نشد» را
   * `reportBrokenImage` (lib/report.ts) از هر دو اپ می‌فرستد.
   */
  eventPhotos: () => call<EventPhotos>('admin_event_photos', {}),
  eventMultiPhotos: () => call<EventMultiPhotos>('admin_event_multi_photos', {}),
  /** عکس‌های مشکوک از روی گزارشِ کاربران + نتیجه‌ی تستِ سرور (0161). */
  suspectImages: () => call<SuspectImages>('admin_suspect_images', {}),

  /**
   * آدرسِ رویدادها — واقعیتِ خام (مایگریشن 0140).
   *
   * ⚠ این جواب **گزارش نیست**، خوراکِ گزارش است: هیچ‌جایش نمی‌گوید یک آدرس
   * خوب است یا بد. حکم کارِ `lib/addressReport.buildAddressReport` است، به
   * همان دلیلی که `journeys()` نمودارش را در SQL نمی‌سازد — آستانه‌ها
   * تصمیمِ نمایشی‌اند و عوض کردنشان نباید مایگریشن بخواهد.
   *
   * سقف روی فهرستِ آدرس‌هاست و نه روی شمارش‌ها؛ `totals.capped` می‌گوید به
   * سقف خورده یا نه.
   */
  eventAddresses: (limit = 3000) =>
    call<EventAddresses>('admin_event_addresses', { p_limit: limit }),

  /**
   * واژه‌نامه‌ی آدرسِ کرالر (`crawler.alias_entries`، مایگریشن 0152) — هر
   * ردیف کامل، با وضعیتِ روشن/خاموشش. کدام ستون حکم است و کدام شمارنده را
   * `lib/aliasReport.buildAliasReport` می‌گوید.
   */
  aliasEntries: (limit = 5000) =>
    call<AliasEntriesData>('admin_alias_entries', { p_limit: limit }),

  /** رویدادهای دورریخته‌ی بیرون از پاکت (WHOSUP-286، مایگریشن 0157) — فقط‌خواندنی. */
  outOfBagEvents: () => call<OutOfBagReport>('admin_out_of_bag_events', {}),

  /**
   * آخرین استفاده‌های یک جفتِ واژه‌نامه از `crawler.alias_usage` (مایگریشن
   * 0156)، با کلیدِ `raw_hash` خودِ مدخل. تنبل: فقط وقتی جفتی باز می‌شود.
   */
  aliasUsage: (rawHash: string, limit = 20) =>
    call<AliasUsageData>('admin_alias_usage', { p_raw_hash: rawHash, p_limit: limit }),

  /**
   * یک ردیفِ واژه‌نامه را روشن/خاموش می‌کند. ⚠ تا کرالر
   * `public.alias_entry_switches` را نکشد و رعایت نکند، اثری روی آدرس‌ها
   * ندارد — `plan/prompts/crawler-alias-usage.md`.
   */
  setAliasEnabled: (key: string, enabled: boolean) =>
    call<{ key: string; enabled: boolean }>(
      'admin_set_alias_enabled', { p_key: key, p_enabled: enabled },
    ),

  /**
   * جدولِ تفکیکِ آدرس (مایگریشن 0157، WHOSUP-277) — گزارشِ `address_table`
   * کرالر + شمارِ رویداد و فلگ‌های زنده. قرمزها را `lib/addressTable` می‌گذارد.
   */
  addressTable: () => call<AddressTableData>('admin_address_table', {}),

  /**
   * ستون‌های غلطِ یک ردیف را در `crawler.address_flags` می‌نویسد. فهرستِ
   * خالی یعنی «هیچ‌کدام غلط نیست» — ردیف پاک نمی‌شود، چون `cloud pull`
   * کرالر پاک شدن را نمی‌بیند.
   */
  setAddressFlags: (city: string, address: string, columns: string[], note?: string | null) =>
    call<{ city: string; address: string; columns: string[] }>(
      'admin_set_address_flags',
      { p_city: city, p_address: address, p_columns: columns, p_note: note ?? null },
    ),

  /**
   * پرامپت‌های کرالر (مایگریشن 0142) — رجیستری + آمارِ تماس + ردِ پای مدل.
   *
   * ⚠ باز هم واقعیت و نه گزارش: «این پرامپت خراب است» را
   * `lib/promptReport.buildPromptReport` می‌گوید، نه SQL. همان دلیلِ
   * `eventAddresses` بالا.
   *
   * ⚠ **متنِ پرامپت این‌جا نمی‌آید، فقط طولش.** متنِ چند ده کیلوبایتیِ ده
   * پرامپت یعنی جوابی که هر بار باز کردنِ سربرگ می‌کشدش، برای چیزی که
   * یکی‌اش هم خوانده نمی‌شود. متن و تماس‌های واقعی در `promptDetail` است.
   */
  prompts: () => call<PromptsData>('admin_prompts', {}),

  /**
   * یک پرامپت: متنِ کاملش، و آخرین تماس‌هایش با ورودی و خروجیِ واقعی.
   *
   * سقف ۲۰ است چون متنِ بیش از بیست تماس هم نگه داشته نمی‌شود (تریگرِ
   * ۰۱۴۲ تهی‌شان می‌کند)، پس عددِ بزرگ‌تر فقط ردیفِ بی‌متن می‌آورد.
   */
  promptDetail: (key: string, limit = 20) =>
    call<PromptDetail>('admin_prompt_detail', { p_key: key, p_limit: limit }),

  /**
   * متنِ واقعیِ یکی از سه ردِ پای مدل، با نمونه‌های واقعی (مایگریشن 0143).
   *
   * ⚠ این **به رجیستری بند نیست**: متن را از همان‌جایی می‌خواند که کرالر
   * از قبل ذخیره‌اش می‌کند (`crawler.discovery_prompts.body`،
   * `crawler.building_geocodes.asked`). یعنی بدونِ یک خط کدِ تازه در آن
   * مخزن هم جواب می‌دهد — که کلِ دلیلِ وجودش است.
   *
   * غربالگری متن ندارد و `prompt` تهی برمی‌گردد؛ چرایی‌اش در
   * `TRACE_META.inspect.noPromptWhy`.
   */
  promptTrace: (key: TraceKey, limit = 5) =>
    call<TraceDetail>('admin_prompt_trace', { p_key: key, p_limit: limit }),

  /**
   * سلامتِ کلِ سیستم — واقعیت‌های امروز + عکس‌های روزانه‌ی ۶۰ روز
   * (مایگریشن 0153، WHOSUP-264).
   *
   * ⚠ باز هم واقعیت و نه حکم: «این عقربه قرمز است» و «بهتر شده» را
   * `lib/healthReport.buildHealthReport` می‌گوید، نه SQL.
   *
   * ⚠ این یکی **می‌نویسد**: هر صدا زدنش عکسِ امروز را در `health_snapshots`
   * تازه می‌کند تا فردا چیزی برای مقایسه باشد. یک ردیف در روز، upsert —
   * پس تازه کردنِ صفحه ردیف نمی‌سازد. از 0169 (WHOSUP-324) اگر عکسِ امروز
   * کمتر از یک ساعت پیش گرفته شده همان خوانده می‌شود؛ `refresh` از نو حساب می‌کند.
   */
  health: (refresh = false) => call<HealthData>('admin_health', { p_refresh: refresh }),

  /**
   * تا ۱۰ ردیفِ واقعی پشتِ یک عقربه، برای پنجره‌ی راهنما (مایگریشن 0154).
   * فقط وقتی پنجره باز می‌شود و فقط برای همان عقربه — نه با `health`.
   */
  healthSamples: (key: string) =>
    call<{ rows: HealthSample[] }>('admin_health_samples', { p_key: key }),

  /** شهرها و کشورهایی که «بارِ شهر» نمی‌شمارد (مایگریشن 0159). */
  healthExclusions: () => call<HealthExclusions>('admin_health_exclusions', {}),

  /** یک شهر (`cityId`) یا یک کشور (`country`، کدِ دوحرفی) را حذف/برمی‌گرداند. */
  setHealthExclusion: (place: { cityId: number } | { country: string }, on: boolean) =>
    call<null>('admin_set_health_exclusion', {
      p_city_id: 'cityId' in place ? place.cityId : null,
      p_country: 'country' in place ? place.country : null,
      p_on: on,
    }),
};
